import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  XCircle,
  Loader
} from 'lucide-react'
import { useProfile } from '@/hooks/useProfile'
import { useIsMobile } from '@/hooks/use-mobile'
import { UpdateProfileRequest } from '@/types/profile'
import { formatCurrency, parseFlexibleInput } from '@/utils'
import { PageTemplate } from '@/components/templates'
import { Switch } from '@/components/ui/switch'
import { ProfileTabs, isProfileTabId, type ProfileTabId } from '@/components/molecules'
import InquiriesTable from '@/components/molecules/InquiriesTable'
import { OrganizationSection } from '@/components/organisms/organization'
import { CONTAINER_SIZES, INDUSTRIES_OF_INTEREST } from '@/constants'
import { notificationService } from '@/services/notificationService'
import type { NotificationSettings } from '@/types/notifications'
import { ProfileScreenDesktop } from './profile/ProfileScreenDesktop'
import { ProfileScreenMobile } from './profile/ProfileScreenMobile'
import { ProfileInterestsDesktop } from './profile/ProfileInterestsDesktop'
import { ProfileInterestsMobile } from './profile/ProfileInterestsMobile'

type ActiveTab = ProfileTabId

const ProfileScreen = () => {
  const isMobile = useIsMobile()
  const { profile, loading, updating, uploadingPicture, error, updateProfile, uploadProfilePicture } = useProfile()

  // Form state
  const [formData, setFormData] = useState<UpdateProfileRequest>({})
  const [successMessage, setSuccessMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Tab selection lives in the URL (`/profile?tab=organization`) rather than in
  // component state, so the Organization tab can be linked to directly - e.g.
  // from an accepted invitation - and survives a refresh or a back/forward.
  // `profile` is the default and is left out of the query string entirely, so
  // existing links to a bare `/profile` are unaffected.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: ActiveTab = isProfileTabId(tabParam) ? tabParam : 'profile'

  const setActiveTab = useCallback((tab: ActiveTab) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous)
        if (tab === 'profile') {
          next.delete('tab')
        } else {
          next.set('tab', tab)
        }
        return next
      },
      // Switching tabs should not stack history entries between the user and
      // whatever page they arrived from.
      { replace: true },
    )
  }, [setSearchParams])

  // Settings state
  const [isSettingsLoading, setIsSettingsLoading] = useState(true)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null)
  const [desktopBuyInput, setDesktopBuyInput] = useState('')
  const [desktopSellInput, setDesktopSellInput] = useState('')
  const [isAddingDesktopBuy, setIsAddingDesktopBuy] = useState(false)
  const [isAddingDesktopSell, setIsAddingDesktopSell] = useState(false)
  const [isUpdatingDesktopIndustries, setIsUpdatingDesktopIndustries] = useState(false)

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        country: profile.country || '',
        preferred_investment_amount_min: profile.preferred_investment_amount_min,
        preferred_investment_amount_max: profile.preferred_investment_amount_max,
        x_account: profile.x_account || '',
        linkedin: profile.linkedin || '',
        company_name: profile.company_name || '',
        job_title: profile.job_title || '',
        email: profile.email,
        // New preference fields
        companies_of_interest_buy: profile.companies_of_interest_buy || [],
        companies_of_interest_sell: profile.companies_of_interest_sell || [],
        industries_of_interest: profile.industries_of_interest || []
      })
    }
  }, [profile])

  // Load settings when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings') {
      loadSettings()
    }
  }, [activeTab])

  const loadSettings = async () => {
    try {
      setIsSettingsLoading(true)
      setSettingsError(null)
      const settings = await notificationService.getSettings()
      setNotificationSettings(settings)
    } catch (err) {
      setSettingsError('Failed to load settings')
      console.error(err)
    } finally {
      setIsSettingsLoading(false)
    }
  }

  // Handle form field changes
  const handleInputChange = (field: keyof UpdateProfileRequest, value: string | number | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Handle profile picture upload
  const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      await uploadProfilePicture(file)
      setSuccessMessage('Profile picture updated successfully!')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      setValidationErrors(prev => ({ 
        ...prev, 
        profile_picture: err instanceof Error ? err.message : 'Failed to upload profile picture' 
      }))
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Get profile picture URL
  const getProfilePictureUrl = () => {
    return profile?.profile_picture_url
  }

  // Get user initials for fallback avatar
  const getUserInitials = () => {
    const first = profile?.first_name?.[0] || ''
    const last = profile?.last_name?.[0] || ''
    return (first + last).toUpperCase() || 'U'
  }

  // Handle interest management
  const handleAddBuyInterest = async (newInterests: string[]) => {
    const updatedInterests = [...(formData.companies_of_interest_buy || []), ...newInterests]
    const updatedData = { ...formData, companies_of_interest_buy: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleRemoveBuyInterest = async (interestToRemove: string) => {
    const updatedInterests = (formData.companies_of_interest_buy || []).filter(i => i !== interestToRemove)
    const updatedData = { ...formData, companies_of_interest_buy: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleAddSellInterest = async (newInterests: string[]) => {
    const updatedInterests = [...(formData.companies_of_interest_sell || []), ...newInterests]
    const updatedData = { ...formData, companies_of_interest_sell: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleRemoveSellInterest = async (interestToRemove: string) => {
    const updatedInterests = (formData.companies_of_interest_sell || []).filter(i => i !== interestToRemove)
    const updatedData = { ...formData, companies_of_interest_sell: updatedInterests }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const handleUpdateIndustries = async (industries: string[]) => {
    const updatedData = { ...formData, industries_of_interest: industries }
    await updateProfile(updatedData)
    setFormData(updatedData)
  }

  const getNewUniqueInterests = (rawInput: string, existingInterests: string[]) => {
    const parsedInterests = parseFlexibleInput(rawInput)
    if (parsedInterests.length === 0) {
      return null
    }

    const duplicates = parsedInterests.filter((interest) =>
      existingInterests.some((existing) => existing.toLowerCase() === interest.toLowerCase())
    )

    if (duplicates.length > 0) {
      alert(`Already have: ${duplicates.join(', ')}`)
      return null
    }

    return parsedInterests
  }

  const handleDesktopAddBuyInterests = async () => {
    const uniqueInterests = getNewUniqueInterests(desktopBuyInput, formData.companies_of_interest_buy || [])
    if (!uniqueInterests) {
      return
    }

    try {
      setIsAddingDesktopBuy(true)
      await handleAddBuyInterest(uniqueInterests)
      setDesktopBuyInput('')
    } finally {
      setIsAddingDesktopBuy(false)
    }
  }

  const handleDesktopAddSellInterests = async () => {
    const uniqueInterests = getNewUniqueInterests(desktopSellInput, formData.companies_of_interest_sell || [])
    if (!uniqueInterests) {
      return
    }

    try {
      setIsAddingDesktopSell(true)
      await handleAddSellInterest(uniqueInterests)
      setDesktopSellInput('')
    } finally {
      setIsAddingDesktopSell(false)
    }
  }

  const handleDesktopIndustryToggle = async (industry: string, checked: boolean) => {
    const currentIndustries = formData.industries_of_interest || []
    const updatedIndustries = checked
      ? Array.from(new Set([...currentIndustries, industry]))
      : currentIndustries.filter((item) => item !== industry)

    try {
      setIsUpdatingDesktopIndustries(true)
      await handleUpdateIndustries(updatedIndustries)
    } finally {
      setIsUpdatingDesktopIndustries(false)
    }
  }

  const handleDesktopSelectAllIndustries = async () => {
    try {
      setIsUpdatingDesktopIndustries(true)
      await handleUpdateIndustries([...INDUSTRIES_OF_INTEREST])
    } finally {
      setIsUpdatingDesktopIndustries(false)
    }
  }

  const handleDesktopClearAllIndustries = async () => {
    try {
      setIsUpdatingDesktopIndustries(true)
      await handleUpdateIndustries([])
    } finally {
      setIsUpdatingDesktopIndustries(false)
    }
  }

  // Handle notification settings
  const handleNotificationToggle = async (enabled: boolean) => {
    try {
      // Optimistic update
      if (notificationSettings) {
        setNotificationSettings({
          ...notificationSettings,
          email_notifications: enabled
        })
      }
      
      // Make API call
      if (notificationSettings) {
        const updated = await notificationService.updateSettings({
          ...notificationSettings,
          email_notifications: enabled
        })
        setNotificationSettings(updated)
      }
    } catch (err) {
      // Revert optimistic update on error
      if (notificationSettings) {
        setNotificationSettings({
          ...notificationSettings,
          email_notifications: !enabled
        })
      }
      setSettingsError('Failed to update notification settings')
      console.error(err)
    }
  }

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format'
    }

    if (formData.preferred_investment_amount_min && formData.preferred_investment_amount_max) {
      if (formData.preferred_investment_amount_min >= formData.preferred_investment_amount_max) {
        errors.preferred_investment_amount_max = 'Maximum amount must be greater than minimum'
      }
    }

    if (formData.linkedin && !formData.linkedin.includes('linkedin.com')) {
      errors.linkedin = 'Please enter a valid LinkedIn URL'
    }

    if (formData.x_account && !formData.x_account.startsWith('@')) {
      errors.x_account = 'X account should start with @'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await updateProfile(formData)
      setSuccessMessage('Profile updated successfully!')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      // Error is already handled in the hook
    }
  }

  const desktopInputClass = '!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-white !pr-[18px] !py-[14px] font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] placeholder:!text-[rgba(45,41,35,0.64)] focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:!border-[rgba(253,253,253,0.12)] dark:!bg-[#1c1c1c] dark:text-[rgba(253,253,253,0.72)] dark:placeholder:!text-[rgba(253,253,253,0.72)] dark:focus:!border-[rgba(253,253,253,0.12)]'
  const mobileInputClass = '!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] !bg-[var(--background-lighter)] !pr-[18px] !py-[14px] font-plus-jakarta !text-[14px] !font-medium !leading-[20px] !text-[var(--text)] placeholder:!text-[var(--text)] placeholder:opacity-60 focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:!border-[rgba(253,253,253,0.14)] dark:focus:!border-[rgba(253,253,253,0.22)]'

  const formatCurrencyDesktop = (amount: number | null | undefined): string => {
    const formatted = formatCurrency(amount)
    return formatted === '—' ? 'Not specified' : formatted.replace(/,/g, ' ')
  }

  const hasMinAmount = profile?.preferred_investment_amount_min !== null && profile?.preferred_investment_amount_min !== undefined
  const hasMaxAmount = profile?.preferred_investment_amount_max !== null && profile?.preferred_investment_amount_max !== undefined
  const countryDisplay =
    profile?.country === 'United States'
      ? 'USA'
      : profile?.country || 'Not specified'
  const selectedIndustries = formData.industries_of_interest || []
  const industryLabelMap: Record<string, string> = {
    'DeFi (Decentralized Finance)': 'DeFi (Decentralised Finance)',
  }
  const profileViewProps = {
    profile,
    formData,
    uploadingPicture,
    updating,
    validationErrors,
    fileInputRef,
    getProfilePictureUrl,
    getUserInitials,
    onProfilePictureChange: handleProfilePictureChange,
    onInputChange: handleInputChange,
    onSubmit: handleSubmit,
    desktopInputClass,
    mobileInputClass,
    hasMinAmount,
    hasMaxAmount,
    countryDisplay,
    formatCurrencyDesktop,
    industryLabelMap,
  }
  const interestsViewProps = {
    formData,
    updating,
    desktopBuyInput,
    desktopSellInput,
    isAddingDesktopBuy,
    isAddingDesktopSell,
    isUpdatingDesktopIndustries,
    selectedIndustries,
    industryLabelMap,
    onDesktopBuyInputChange: setDesktopBuyInput,
    onDesktopSellInputChange: setDesktopSellInput,
    onAddBuyInterests: handleDesktopAddBuyInterests,
    onAddSellInterests: handleDesktopAddSellInterests,
    onRemoveBuyInterest: handleRemoveBuyInterest,
    onRemoveSellInterest: handleRemoveSellInterest,
    onIndustryToggle: handleDesktopIndustryToggle,
    onSelectAllIndustries: handleDesktopSelectAllIndustries,
    onClearAllIndustries: handleDesktopClearAllIndustries,
  }

  if (loading) {
    return (
      <PageTemplate>
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center gap-3">
            <Loader className="animate-spin text-primary-500" size={24} />
            <span className="text-[var(--text)]">Loading profile...</span>
          </div>
        </div>
      </PageTemplate>
    )
  }

  if (error && !profile) {
    return (
      <PageTemplate>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <XCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-[var(--text)] mb-2">Error Loading Profile</h2>
            <p className="text-[var(--text)] opacity-60">{error}</p>
          </div>
        </div>
      </PageTemplate>
    )
  }

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="font-plus-jakarta text-[30px] font-semibold leading-[40px] tracking-[-0.28px] text-[#2d2923] dark:text-[#fdfdfd]">
            Profile & Settings
          </h1>
          <p className="font-plus-jakarta text-[16px] font-normal leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            Manage your personal information, interests, and preferences
          </p>
        </div>
        
        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 flex items-center gap-2 rounded-lg"
          >
            <CheckCircle size={18} />
            <span className="text-sm">{successMessage}</span>
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 flex items-center gap-2 rounded-lg"
          >
            <XCircle size={18} />
            <span className="text-sm">{error}</span>
          </motion.div>
        )}

        {/* Tab Navigation */}
        <ProfileTabs 
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {activeTab === 'profile' && (
          isMobile ? <ProfileScreenMobile {...profileViewProps} /> : <ProfileScreenDesktop {...profileViewProps} />
        )}

        {/* Investment Interests Tab */}
        {activeTab === 'interests' && (
          isMobile ? <ProfileInterestsMobile {...interestsViewProps} /> : <ProfileInterestsDesktop {...interestsViewProps} />
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <InquiriesTable isMobile={isMobile} />
        )}

        {/* Organization Tab */}
        {activeTab === 'organization' && (
          <OrganizationSection isMobile={isMobile} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            {isSettingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin text-primary-500" size={24} />
                <span className="ml-2 text-[var(--text)]">Loading settings...</span>
              </div>
            ) : isMobile ? (
              <div className="border border-white bg-[rgba(255,255,255,0.32)] px-3 py-3 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
                {settingsError && (
                  <div className="mb-4 bg-red-100 border border-red-400 px-4 py-3 text-red-700">
                    {settingsError}
                  </div>
                )}

                <h2 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Notification Preferences
                </h2>

                <div className="mt-4 border border-[rgba(45,41,35,0.12)] bg-white px-3 py-3 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
                  <h3 className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                    Email Notifications
                  </h3>
                  <p className="mt-1 font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    Receive notifications about your investments and market updates
                  </p>
                  <Switch
                    checked={notificationSettings?.email_notifications || false}
                    onCheckedChange={handleNotificationToggle}
                    className="mt-3 data-[state=checked]:bg-[#2d2923] data-[state=unchecked]:bg-[rgba(45,41,35,0.24)] dark:data-[state=checked]:bg-[#fdfdfd] dark:data-[state=unchecked]:bg-[rgba(253,253,253,0.24)] dark:border-transparent dark:[&>span]:!bg-[#1c1c1c]"
                  />
                </div>

                <h2 className="mt-8 font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Additional Settings
                </h2>
                <p className="mt-[12px] font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  More settings will be available here in future updates.
                </p>
              </div>
            ) : (
              <div className="border border-white bg-[rgba(255,255,255,0.32)] px-8 py-8 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
                {settingsError && (
                  <div className="mb-4 bg-red-100 border border-red-400 px-4 py-3 text-red-700">
                    {settingsError}
                  </div>
                )}

                <h2 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Notification Preferences
                </h2>

                <div className="mt-6 flex items-center justify-between border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(45,41,35,0.12)] dark:bg-[#1c1c1c]">
                  <div>
                    <h3 className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                      Email Notifications
                    </h3>
                    <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      Receive notifications about your investments and market updates
                    </p>
                  </div>

                  <Switch
                    checked={notificationSettings?.email_notifications || false}
                    onCheckedChange={handleNotificationToggle}
                    className="data-[state=checked]:bg-[#2d2923] data-[state=unchecked]:bg-[rgba(45,41,35,0.24)] dark:data-[state=checked]:bg-[#fdfdfd] dark:data-[state=unchecked]:bg-[rgba(253,253,253,0.24)] dark:border-transparent dark:[&>span]:!bg-[#1c1c1c]"
                  />
                </div>

                <h2 className="mt-8 font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                  Additional Settings
                </h2>
                <p className="mt-[12px] font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  More settings will be available here in future updates.
                </p>
              </div>
            )}
          </div>
        )}

      </div>
    </PageTemplate>
  )
}

export default ProfileScreen
